import { useState, useCallback, useEffect, useRef } from 'react';
import { ChatMessage } from '../domain/PatientChat';
import { CouplePatientChatApiService } from '../infrastructure/api/CouplePatientChatApiService';
import { CouplePatientChatHistoryApiService } from '../infrastructure/api/CouplePatientChatHistoryApiService';

interface UseCouplePatientChatOptions {
  persist?: boolean;
}

export const useCouplePatientChat = (couplePatientId: string, options: UseCouplePatientChatOptions = {}) => {
  const { persist = false } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset/load conversation when couple patient changes
  useEffect(() => {
    setMessages([]);
    setError(null);

    if (persist && couplePatientId) {
      CouplePatientChatHistoryApiService.load(couplePatientId)
        .then((loaded) => setMessages(loaded))
        .catch(() => {/* silently ignore */});
    }
  }, [couplePatientId, persist]);

  // Abort any in-flight request on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;

      const userMessage: ChatMessage = { role: 'user', content: text };
      const currentHistory = messages;

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await CouplePatientChatApiService.sendMessage(couplePatientId, {
          message: text,
          history: currentHistory,
        });
        const assistantMessage: ChatMessage = {
          role: 'model',
          content: response.data.response,
        };
        const updatedMessages = [...currentHistory, userMessage, assistantMessage];
        setMessages(updatedMessages);

        if (persist) {
          CouplePatientChatHistoryApiService.save(couplePatientId, updatedMessages).catch(() => {/* silently ignore */});
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        setError('chat.error');
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [couplePatientId, messages, persist],
  );

  return { messages, isLoading, error, sendMessage };
};
